"use client"

import { Fragment } from "react"
import { cn } from "@strivelabs/ui-core"
import { Tooltip } from "@strivelabs/ui-core/v2"
import { useSidebar } from "./context"
import type { NavSection } from "./types"

export interface NavRailProps {
  sections: NavSection[]
}

/**
 * Icon-only rendering of the top level, shown while the sidebar is collapsed.
 *
 * Nested nodes are unreachable here by design — expand the sidebar to reach
 * them. Each control carries its own `aria-label` because the tooltip supplies
 * `aria-describedby`, which is a description rather than an accessible name.
 */
export function NavRail({ sections }: NavRailProps) {
  const { currentPath, renderLink } = useSidebar()

  return (
    <ul className="flex flex-col items-center gap-1">
      {sections.map((section, index) => (
        <Fragment key={section.id}>
          {index > 0 ? (
            <li aria-hidden className="my-1 h-px w-5 bg-v2-sidebar-border" />
          ) : null}
          {section.items.map(node => {
            const active = node.match
              ? node.match(currentPath)
              : node.href !== undefined && node.href !== "#" && currentPath === node.href
            const className = cn(
              "grid size-8 place-items-center rounded-v2-md text-v2-sidebar-foreground",
              "transition-colors hover:bg-v2-sidebar-accent",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring",
              active && "bg-v2-sidebar-accent text-v2-sidebar-accent-foreground",
              node.disabled && "pointer-events-none opacity-50",
            )
            return (
              <li key={node.id}>
                <Tooltip label={node.label} side="right">
                  {node.href && node.href !== "#" && !node.disabled
                    ? renderLink({
                        href: node.href,
                        className,
                        children: (
                          <>
                            <span aria-hidden>{node.icon}</span>
                            <span className="sr-only">{node.label}</span>
                          </>
                        ),
                        "aria-current": active ? "page" : undefined,
                        onClick: node.onSelect,
                      })
                    : (
                      <button
                        type="button"
                        aria-label={node.label}
                        onClick={node.onSelect}
                        disabled={node.disabled}
                        className={className}
                      >
                        {node.icon}
                      </button>
                    )}
                </Tooltip>
              </li>
            )
          })}
        </Fragment>
      ))}
    </ul>
  )
}
